/**
 * Sanitizes HTML content before saving a post/communication.
 *
 * Authors use blank lines (an empty block such as `<p><br></p>` or `<h3><br></h3>`,
 * produced by pressing Enter) as deliberate vertical spacing, so we must preserve a
 * single blank line. We only guard against *runaway* spacing:
 *   - collapse any run of 2+ consecutive blank lines down to a single one
 *   - strip leading/trailing blank lines (never intentional content)
 *
 * Called only at save time to avoid janky editor behavior during editing.
 *
 * @param html - The HTML string to sanitize
 * @returns Cleaned HTML string with at most one consecutive blank line preserved
 */
export const sanitizeContentForSave = (html: string): string => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html || '', 'text/html')
    const body = doc.body

    /**
     * Normalize text content for comparison
     * - Replaces U+00A0 (non-breaking space / &nbsp;) with a regular space
     * - Removes U+200B (zero-width space) characters
     * - Trims whitespace from both ends
     */
    const normalizedText = (node: Node) =>
      (node.textContent || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\u200b/g, '')
        .trim()

    /**
     * A "blank line" is a block element with no visible text whose only children
     * are <br> tags and/or whitespace (e.g. <p><br></p>, <h3><br></h3>).
     */
    const isBlankLine = (node: Node): boolean => {
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return false
      }
      const el = node as Element
      if (normalizedText(el) !== '') {
        return false
      }
      return Array.from(el.childNodes).every((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          return normalizedText(child) === ''
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          return (child as Element).tagName.toLowerCase() === 'br'
        }
        return true
      })
    }

    // Walk top-level blocks, collapsing consecutive blank lines to a single one
    // and dropping leading blank lines.
    const blocks = Array.from(body.children)
    let prevWasBlank = true // treat start-of-content as blank so leading blanks drop
    blocks.forEach((block) => {
      if (isBlankLine(block)) {
        if (prevWasBlank) {
          // leading blank, or a second+ blank in a run → remove
          block.remove()
        } else {
          prevWasBlank = true
        }
      } else {
        prevWasBlank = false
      }
    })

    // Drop any trailing blank line(s) left at the very end
    while (body.lastElementChild && isBlankLine(body.lastElementChild)) {
      body.lastElementChild.remove()
    }

    return body.innerHTML
  } catch (error) {
    console.error('🚨 sanitizeContentForSave: HTML parsing failed', error)
    return html || ''
  }
}
